import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import SEO from '../components/SEO';

function Blog() {
  const blogPosts = [
    {
      id: 1,
      title: 'How to Protect Your Privacy Online with Disposable Emails',
      excerpt: 'Learn how temporary email addresses can help protect your personal information from spam and data breaches.',
      date: '2024-01-15',
      category: 'Privacy',
      readTime: '5 min read'
    },
    {
      id: 2,
      title: '10 Best Practices for Using Temporary Email Services',
      excerpt: 'Discover the best ways to use disposable email addresses safely and effectively for your online activities.',
      date: '2024-01-10',
      category: 'Tips',
      readTime: '4 min read'
    },
    {
      id: 3,
      title: 'Understanding Email Privacy: Why It Matters',
      excerpt: 'Explore the importance of email privacy in today\'s digital world and how disposable emails can help.',
      date: '2024-01-05',
      category: 'Privacy',
      readTime: '6 min read'
    },
    {
      id: 4,
      title: 'The Future of Email Security and Privacy',
      excerpt: 'A look at emerging trends in email security and how temporary email services are evolving.',
      date: '2024-01-01',
      category: 'Technology',
      readTime: '7 min read'
    }
  ];

  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setStatus('error');
      setMessage('Please enter your email address');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim() }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setStatus('success');
        setMessage(data.message);
        setEmail('');
      } else {
        setStatus('error');
        setMessage(data.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setStatus('error');
      setMessage('Failed to connect. Please try again later.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Blog - FreeDisposableEmail.com | Email Privacy & Security Tips"
        description="Stay informed about email privacy, security tips, and the latest updates from FreeDisposableEmail.com. Learn how to protect your online privacy with temporary emails."
        keywords="email privacy blog, temporary email tips, email security, privacy guide, disposable email blog"
      />
      <Navigation />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Blog</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Stay informed about email privacy, security tips, and the latest updates from FreeDisposableEmail.com
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post) => (
            <article
              key={post.id}
              className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">
                    {post.category}
                  </span>
                  <span className="text-sm text-gray-500">{post.readTime}</span>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2 hover:text-primary-600 transition-colors">
                  <Link to={`/blog/${post.id}`}>{post.title}</Link>
                </h2>
                <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <time dateTime={post.date}>
                    {new Date(post.date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </time>
                  <Link
                    to={`/blog/${post.id}`}
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Read more →
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Newsletter Subscription Section */}
        <div className="mt-16 bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl shadow-xl p-8 md:p-12 text-center max-w-6xl mx-auto border border-primary-200">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              📬 Stay in the Loop
            </h2>
            <p className="text-lg text-gray-700 mb-8">
              Subscribe to our newsletter to get notified when we publish new content about privacy, security, and digital safety.
            </p>
            
            {status === 'success' ? (
              <div className="bg-green-50 border-2 border-green-500 text-green-800 p-6 rounded-xl inline-block animate-fadeIn">
                <div className="flex items-center justify-center gap-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="font-semibold text-lg">{message}</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
                <div className="flex-1">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setStatus('idle'); setMessage(''); }}
                    placeholder="Enter your email address"
                    required
                    disabled={status === 'loading'}
                    className="w-full px-5 py-4 rounded-xl border-2 border-primary-300 focus:ring-4 focus:ring-primary-200 focus:border-primary-500 outline-none transition-all text-gray-900 placeholder-gray-500 disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
                  />
                </div>
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold rounded-xl hover:from-primary-700 hover:to-primary-800 focus:ring-4 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0"
                >
                  {status === 'loading' ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Subscribing...
                    </span>
                  ) : (
                    'Subscribe'
                  )}
                </button>
              </form>
            )}
            
            {status === 'error' && message && (
              <div className="mt-4 bg-red-50 border-2 border-red-300 text-red-700 p-4 rounded-xl animate-fadeIn">
                <p className="font-medium">{message}</p>
              </div>
            )}
            
            <p className="mt-6 text-sm text-gray-600 flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              We respect your privacy. Unsubscribe at any time.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Blog;
